import React, { useEffect, useMemo, useState } from 'react';
import { Video, Plus, Search, LayoutGrid, List, Calendar as CalIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mccGet, mccPatch, mccDelete } from './mccApi';
import { SectionHeader, EmptyState, StatusPill, PlatformIcon, Avatar } from './mccUi';
import { FILMING_STAGES, statusTone, humanDate } from './mccConstants';
import MccKanban from './MccKanban';
import MccCreateDialog from './MccCreateDialog';
import MccStatusGroup from './MccStatusGroup';
import MccInlineFootageUploader from './MccInlineFootageUploader';

export default function MccFilming({ team, onNav }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [filters, setFilters] = useState({ status: '', q: '' });
  const [openDialog, setOpenDialog] = useState(false);
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try { const res = await mccGet('/marketing/filming', { status: filters.status }); setItems(res.items || []); }
    catch (e) { toast.error(e.message || 'Failed to load filming'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status]);

  const filtered = useMemo(() => {
    if (!filters.q) return items;
    const ql = filters.q.toLowerCase();
    return items.filter((i) =>
      (i.title || '').toLowerCase().includes(ql) ||
      (i.person_being_filmed || '').toLowerCase().includes(ql) ||
      (i.topic || '').toLowerCase().includes(ql)
    );
  }, [items, filters.q]);

  const advance = async (f, status) => {
    try { await mccPatch(`/marketing/filming/${f.id}`, { status }); toast.success(`Moved to ${statusTone(status).label}`); await load(); }
    catch (e) { toast.error(e.message || 'Update failed'); }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete filming "${f.title}"?`)) return;
    try { await mccDelete(`/marketing/filming/${f.id}`); await load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  // Adapt filming records to fit the MccContentCard shape.
  const asCardItems = filtered.map((f) => ({
    ...f,
    priority: 'normal',
    assigned_to: [f.responsible_filmer_id, f.script_author_id].filter(Boolean),
    due_date: f.filming_date,
    primary_platform: f.primary_platform,
    additional_platforms: f.additional_platforms,
    hook: f.hook || null,
    versions: [],
    comments: [],
  }));

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Filming Requests"
        subtitle="Organise every filming session — script, talent, hook, location, date and equipment — and hand off to editing when done."
        icon={Video}
        right={<Button onClick={() => setOpenDialog(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="mcc-filming-create">
          <Plus className="w-4 h-4 mr-1" /> Schedule Filming
        </Button>}
      />

      {/* Post-shoot upload panel — link the raw footage straight to a project. */}
      <MccInlineFootageUploader
        mode="raw"
        title="Upload footage from a shoot"
        hint="Just finished filming? Pick the project and drop the raw video, audio or images here. They'll appear in Raw Footage for the editor."
        defaultKind="raw_video"
        onNav={onNav}
        onUploaded={load}
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input className="pl-8 w-64" placeholder="Search filming…" value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
        </div>
        <Select value={filters.status || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {FILMING_STAGES.map((s) => <SelectItem key={s} value={s}>{statusTone(s).label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex rounded-md border border-slate-200 overflow-hidden">
          <button onClick={() => setView('kanban')} className={`px-2.5 py-1.5 ${view === 'kanban' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}><LayoutGrid className="w-3.5 h-3.5" /></button>
          <button onClick={() => setView('list')} className={`px-2.5 py-1.5 ${view === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}><List className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Video} title="No filming scheduled" body="Schedule filming to line up talent, location, script and equipment."
          cta={<Button onClick={() => setOpenDialog(true)}>Schedule Filming</Button>} />
      ) : view === 'kanban' ? (
        <MccKanban stages={FILMING_STAGES} items={asCardItems} variant="request" nameOf={nameOf}
          onOpen={(f) => actOnFilm(items.find((x) => x.id === f.id), advance, del)}
          onMove={(item, newStatus) => advance(items.find((x) => x.id === item.id) || item, newStatus)} />
      ) : (
        <div className="space-y-3">
          {FILMING_STAGES.map((stage) => {
            const rows = filtered.filter((f) => f.status === stage);
            if (rows.length === 0) return null;
            return (
              <MccStatusGroup key={stage} status={stage} count={rows.length}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Title</th>
                      <th className="px-3 py-2 text-left">Person</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Platform</th>
                      <th className="px-3 py-2 text-left">Assigned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((f) => (
                      <tr key={f.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => actOnFilm(f, advance, del)}>
                        <td className="px-3 py-2 text-slate-700">
                          <span className="font-bold text-violet-700">{humanDate(f.filming_date)}</span>
                          {f.filming_time ? <span className="text-[11px] text-slate-500 block">{f.filming_time}</span> : null}
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-900">{f.title}</td>
                        <td className="px-3 py-2 text-slate-700">{f.person_being_filmed || '—'}</td>
                        <td className="px-3 py-2 text-slate-700">{f.location || '—'}</td>
                        <td className="px-3 py-2">{f.primary_platform ? <PlatformIcon platform={f.primary_platform} size={18} /> : '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            {f.responsible_filmer_id && <Avatar name={nameOf(f.responsible_filmer_id)} size={18} />}
                            {f.script_author_id && <Avatar name={nameOf(f.script_author_id)} size={18} />}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MccStatusGroup>
            );
          })}
        </div>
      )}

      <MccCreateDialog open={openDialog} onClose={() => setOpenDialog(false)} kind="filming" team={team} onCreated={load} />
    </div>
  );
}

function actOnFilm(f, advance, del) {
  if (!f) return;
  const choice = window.prompt(
    `Filming: ${f.title}\n\nType a next step:\n- SCRIPT to mark script ready\n- SCHEDULE to move to scheduled\n- READY to mark ready to film\n- FILMED to mark filmed\n- COMPLETE to close\n- DELETE to remove`, ''
  );
  if (!choice) return;
  const c = choice.trim().toUpperCase();
  if (c === 'SCRIPT') advance(f, 'script_ready');
  else if (c === 'SCHEDULE') advance(f, 'scheduled');
  else if (c === 'READY') advance(f, 'ready_to_film');
  else if (c === 'FILMED') advance(f, 'filmed');
  else if (c === 'COMPLETE') advance(f, 'completed');
  else if (c === 'DELETE') del(f);
}
